"use client"

import {
    Sidebar,
    SidebarContent,
    SidebarFooter,
    SidebarGroup,
    SidebarGroupContent,
    SidebarHeader,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
    SidebarTrigger
} from "@/app/components/common/app-sidebar/AppSidebar";
import {Tabs, TabsList, TabsTrigger} from "@/app/components/common/tab-selector/TabSelector";
import {AppTag} from "@/app/components/common/app-tag/AppTag";
import {SmallTag} from "@/app/components/common/small-tag/SmallTag";
import {cn} from "@/lib/utils";
import {useCategoryMenuItems} from "@/hooks/use-category-menu-items";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from "@/app/components/common/app-select/AppSelect";
import {LANGUAGES} from "@/lib/utils/contants/languages";
import React, {JSX, useMemo} from "react";
import {AppSeparator} from "@/app/components/common/app-separator/AppSeparator";
import {usePathname} from "next/navigation";
import styles from "@/app/css/app-btn.module.css";
import SearchInput from "@/app/components/common/search-input/SearchInput";
import {useLocale, useTranslations} from "next-intl";
import {usePathname as useIntlPathname, Link as IntlLink} from '@/i18n/navigation';
import {MENU_TABS} from "@/lib/utils/contants/menuTabs";
import {useDeferredLocaleSwitch} from "@/app/components/common/lang-selector/useDeferredLocaleSwitch";
import {useTopMenu} from "@/hooks/use-top-menu";
import {useOpenDialog} from "@/lib/dialog-orchestrator/react";
import {DialogsEnum} from "@/lib/enums/DialogsEnum";
import {useZendeskWidget} from "@/hooks/useZendeskWidget";
import {Icon} from "@/app/components/common/Icon";

interface IProps {
    open: boolean;
}

export default function AppAside({open}: IProps) {
    const openDialog = useOpenDialog();
    const pathname = usePathname();
    const intlPathname = useIntlPathname();
    const t = useTranslations();
    const locale = useLocale();
    const {topMenuItems} = useTopMenu();
    // AC-819: category items follow the backoffice-managed per-GEO lobby.
    const categoryMenuItems = useCategoryMenuItems();
    const {onOpenWidget} = useZendeskWidget();

    const activeTab = intlPathname.startsWith('/casino') || intlPathname.startsWith('/games')
        ? '/'
        : intlPathname.startsWith('/sports')
            ? '/sports'
            : intlPathname;

    // AC-883: the locale switch runs after the popup is gone, not while it closes.
    const {open: langOpen, setOpen: setLangOpen, onLocaleChange} = useDeferredLocaleSwitch();
    const activeLanguage = useMemo(() => LANGUAGES.find(i => i.value === locale), [locale]);
    const languageItems = useMemo(() => LANGUAGES.map((i) => (
        <SelectItem key={i.value} value={i.value}>
            <div className={'flex items-center gap-[8px]'}>
                <div>{i.icon}</div>
                {i.label}
            </div>
        </SelectItem>
    )), []);

    return (
        <Sidebar collapsible={'icon'}
                 containerClassName={cn({['max-[1140]:fixed z-10']: open})}>
            <SidebarHeader
                className={cn('pt-[16px] gap-[19px] px-[12px]', {['pb-[12px]']: !open}, {['pb-[16px]']: open})}>
                <div className={cn('flex', {['flex-col gap-[19px]']: !open}, {['gap-[33px]']: open})}>
                    <SidebarTrigger className={cn('grow-0 shrink-0', {'rotate-180': !open})}/>

                    <Tabs className={'grow-1'} value={activeTab}>
                        <TabsList className={cn({['flex flex-col gap-[5px]']: !open}, {['h-[48px]']: open})}>
                            {MENU_TABS.map((i, index) => (
                                <IntlLink locale={locale} href={i.value} key={i.value}
                                          className={cn('w-full', {['pointer-events-none']: index === 1})}
                                          scroll={false}>
                                    <TabsTrigger
                                        aria-controls={`tab-${i.value}`}
                                        id={`tab-${i.value}`}
                                        disabled={index === 1}
                                        className={cn('relative cursor-pointer', {['flex justify-center w-[40px] h-[40px] p-0 items-center']: !open}, {['pointer-events-none']: index === 1})}
                                        value={i.value}>
                                        {open ? t(i.label) : i.icon}
                                        {index === 1 ? (
                                            <SmallTag
                                                className={cn('absolute', {['top-0 right-0']: open}, {['bottom-0']: !open})}>{t('AppAside.soon')}</SmallTag>
                                        ) : null}
                                    </TabsTrigger>
                                </IntlLink>
                            ))}
                        </TabsList>
                    </Tabs>
                </div>

                <SearchInput readOnly={true} inputClassName={'cursor-pointer'} autoComplete={'off'}
                             onClick={() => openDialog(DialogsEnum.SEARCH)}
                             className={cn('cursor-pointer', {['mx-[4px] h-[40px] ']: !open}, {['h-[49px]']: open})}/>
            </SidebarHeader>
            <SidebarContent className={cn({['px-[16px]']: !open}, {['px-[12px]']: open})}>
                <SidebarGroup>
                    <SidebarGroupContent>
                        <SidebarMenu>
                            {topMenuItems.map((item, index) => (
                                <SidebarMenuItem key={index}>
                                    <SidebarMenuButton asChild>
                                        <NavLink open={open} {...item} pathname={intlPathname} index={index}/>
                                    </SidebarMenuButton>
                                </SidebarMenuItem>
                            ))}
                        </SidebarMenu>
                    </SidebarGroupContent>
                </SidebarGroup>

                <div className={'px-[12px] py-[28px]'}>
                    <AppSeparator/>
                </div>

                <SidebarGroup>
                    <SidebarGroupContent>
                        <SidebarMenu>
                            {categoryMenuItems.map((item, index) => (
                                <SidebarMenuItem key={index}>
                                    <SidebarMenuButton asChild>
                                        <NavLink open={open} {...item} pathname={pathname}/>
                                    </SidebarMenuButton>
                                </SidebarMenuItem>
                            ))}
                        </SidebarMenu>
                    </SidebarGroupContent>
                </SidebarGroup>

                <div className={'px-[12px]'}>
                    <AppSeparator/>
                </div>

                <SidebarGroup>
                    <SidebarGroupContent>
                        <SidebarMenu>
                            <SidebarMenuItem>
                                <SidebarMenuButton asChild>
                                    <NavLink open={open} pathname={pathname} icon={<Icon name={'liveSupport'}/>}
                                             title={'AppAside.liveSupport'} onClick={onOpenWidget}/>
                                </SidebarMenuButton>
                            </SidebarMenuItem>
                        </SidebarMenu>
                    </SidebarGroupContent>
                </SidebarGroup>
            </SidebarContent>
            <SidebarFooter className={'p-[12px]'}>
                <Select value={locale} open={langOpen} onOpenChange={setLangOpen} onValueChange={onLocaleChange}>
                    <SelectTrigger
                        aria-controls={locale}
                        className={cn('w-full py-[13px]', {['pl-[20px] pr-[24px]']: open}, {['pl-[10px] h-[48px]']: !open})}>
                        <SelectValue>
                            <div className={cn('flex items-center gap-[10px]')}>
                                {activeLanguage?.icon}
                                {open && activeLanguage?.label}
                            </div>
                        </SelectValue>
                    </SelectTrigger>
                    <SelectContent className={'z-10 min-w-[200px] overflow-y-scroll max-h-[400px]'}>
                        {languageItems}
                    </SelectContent>
                </Select>
            </SidebarFooter>
        </Sidebar>
    )
}

const NavLink = ({href, icon, open, isSoon, title, rawTitle, pathname, index, onClick}: {
    href?: string;
    icon: JSX.Element;
    open: boolean;
    isSoon?: boolean | undefined;
    title: string
    rawTitle?: string;
    pathname: string;
    index?: number;
    onClick?: () => void;
}) => {
    const t = useTranslations();
    return (
        <IntlLink onClick={onClick} href={href || ''} prefetch={false}
                  className={cn(
                      'w-full text-noble-black-300 mb-[16px] flex h-[40px] rounded-[8px] items-center',
                      {['w-[40px] justify-center relative']: !open},
                      {['pointer-events-none']: isSoon},
                      {['gap-[8px] px-[12px]']: open},
                      {[styles.ButtonActiveSimple]: !open && (index !== undefined && index === 0 ? pathname === href : href ? pathname.includes(href) : false)},
                      {[styles.ButtonActive]: open && (index !== undefined && index === 0 ? pathname === href : href ? pathname.includes(href) : false)},
                  )}>
            <div>
                {icon}
            </div>
            {
                open &&
                <span
                    className={'w-full flex justify-between items-center h-full font-medium text-[14px] leading-none'}>
                    {rawTitle || t(title)}
                    {isSoon && <AppTag className={'h-full'}>{t('AppAside.soon')}</AppTag>}
                </span>
            }

            {isSoon && !open && <SmallTag className={'absolute bottom-[-3px]'}>{t('AppAside.soon')}</SmallTag>}
        </IntlLink>
    )
}

